import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Download, RotateCcw, XCircle } from 'lucide-react';
import { orderService } from '@/services/order.service';
import { Order } from '@/types';
import { Breadcrumb } from '@/components/common/Breadcrumb';
import { OrderStatusBadge } from '@/components/common/OrderStatusBadge';
import { OrderTrackingTimeline } from '@/components/common/OrderTrackingTimeline';
import { Skeleton } from '@/components/common/Skeleton';
import { Button } from '@/components/ui/button';
import { formatCurrency, formatDateTime } from '@/utils';
import toast from '@/hooks/useToast';

export const OrderDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [reordering, setReordering] = useState(false);

  useEffect(() => {
    if (!id) return;
    orderService.getOrderDetail(id)
      .then(res => setOrder(res.data.data))
      .finally(() => setLoading(false));
  }, [id]);

  const handleCancel = async () => {
    if (!id || !window.confirm('Are you sure you want to cancel this order?')) return;
    setCancelling(true);
    try {
      await orderService.cancelOrder(id);
      const res = await orderService.getOrderDetail(id);
      setOrder(res.data.data);
      toast.success('Order cancelled');
    } catch { toast.error('Could not cancel order'); }
    finally { setCancelling(false); }
  };

  const handleDownloadInvoice = async () => {
    if (!id) return;
    try {
      const res = await orderService.downloadInvoice(id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${order?.orderNumber}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Could not download invoice'); }
  };

  if (loading) return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
      <Skeleton className="h-6 w-64" />
      <div className="grid md:grid-cols-3 gap-6">
        <Skeleton className="h-48 md:col-span-2" />
        <Skeleton className="h-48" />
      </div>
    </div>
  );

  if (!order) return <div className="text-center py-20 text-gray-500">Order not found</div>;

  const canCancel = ['PENDING', 'CONFIRMED'].includes(order.status);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <Breadcrumb items={[
        { label: 'Home', href: '/' },
        { label: 'My Orders', href: '/orders' },
        { label: `#${order.orderNumber}` },
      ]} />

      <div className="flex items-center justify-between mt-4 mb-6">
        <div>
          <h1 className="font-heading font-bold text-2xl text-gray-800">Order #{order.orderNumber}</h1>
          <p className="text-sm text-gray-400 mt-1">{formatDateTime(order.createdAt)}</p>
        </div>
        <div className="flex items-center gap-3">
          <OrderStatusBadge status={order.status} />
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="md:col-span-2 space-y-6">
          {/* Items */}
          <div className="bg-white rounded-card shadow-card p-6">
            <h2 className="font-heading font-semibold text-gray-800 mb-4">Items Ordered</h2>
            <div className="space-y-4">
              {order.items.map(item => (
                <div key={item.id} className="flex gap-4">
                  <img
                    src={item.product?.images?.[0] || '/placeholder-product.jpg'}
                    alt={item.product?.name}
                    className="w-16 h-16 object-cover rounded-xl"
                  />
                  <div className="flex-1">
                    <p className="font-semibold text-gray-800">{item.product?.name}</p>
                    <p className="text-sm text-gray-400">Qty: {item.quantity} × {formatCurrency(Number(item.unitPrice))}</p>
                  </div>
                  <p className="font-bold text-gray-800">{formatCurrency(Number(item.totalPrice))}</p>
                </div>
              ))}
            </div>

            <div className="border-t border-gray-100 mt-5 pt-4 space-y-2 text-sm">
              <div className="flex justify-between text-gray-500"><span>Subtotal</span><span>{formatCurrency(Number(order.subtotal))}</span></div>
              {Number(order.discount) > 0 && (
                <div className="flex justify-between text-success"><span>Discount</span><span>-{formatCurrency(Number(order.discount))}</span></div>
              )}
              {order.couponCode && (
                <div className="flex justify-between text-gray-400 text-xs"><span>Coupon: {order.couponCode}</span></div>
              )}
              {order.loyaltyPointsUsed > 0 && (
                <div className="flex justify-between text-success text-xs"><span>Milan Points Used ({order.loyaltyPointsUsed} pts)</span><span>-{formatCurrency((order.loyaltyPointsUsed / 100) * 10)}</span></div>
              )}
              <div className="flex justify-between text-gray-500"><span>Delivery</span><span>{Number(order.deliveryCharge) === 0 ? 'FREE' : formatCurrency(Number(order.deliveryCharge))}</span></div>
              <div className="flex justify-between font-bold text-base border-t pt-2"><span>Total</span><span className="text-primary-600">{formatCurrency(Number(order.total))}</span></div>
            </div>
          </div>

          {/* Tracking */}
          {order.tracking && order.tracking.length > 0 && (
            <div className="bg-white rounded-card shadow-card p-6">
              <h2 className="font-heading font-semibold text-gray-800 mb-4">Order Tracking</h2>
              <OrderTrackingTimeline tracking={[...order.tracking].reverse()} />
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Delivery address */}
          {order.deliveryAddress && (
            <div className="bg-white rounded-card shadow-card p-5">
              <h3 className="font-heading font-semibold text-gray-800 mb-3">Delivery Address</h3>
              <p className="text-sm text-gray-600">{order.deliveryAddress.line1}</p>
              {order.deliveryAddress.line2 && <p className="text-sm text-gray-600">{order.deliveryAddress.line2}</p>}
              <p className="text-sm text-gray-600">{order.deliveryAddress.city}, {order.deliveryAddress.state}</p>
              <p className="text-sm text-gray-600">{order.deliveryAddress.pincode}</p>
            </div>
          )}

          {/* Payment */}
          <div className="bg-white rounded-card shadow-card p-5">
            <h3 className="font-heading font-semibold text-gray-800 mb-3">Payment</h3>
            <div className="text-sm text-gray-600 space-y-1">
              <div className="flex justify-between"><span className="text-gray-400">Method</span><span className="capitalize">{order.paymentMethod || 'Razorpay'}</span></div>
              <div className="flex justify-between">
                <span className="text-gray-400">Status</span>
                <span className={`font-semibold ${order.paymentStatus === 'PAID' ? 'text-success' : 'text-orange-500'}`}>{order.paymentStatus}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-2">
            {order.paymentStatus === 'PAID' && (
              <Button variant="outline" className="w-full" onClick={handleDownloadInvoice}>
                <Download className="h-4 w-4 mr-2" /> Download Invoice
              </Button>
            )}
            <Button variant="outline" className="w-full" loading={reordering} onClick={async () => {
              if (!id) return;
              setReordering(true);
              try {
                const res = await orderService.reorder(id);
                toast.success(res.data.message || 'Items added to cart!');
                navigate('/cart');
              } catch { toast.error('Could not reorder'); }
              finally { setReordering(false); }
            }}>
              <RotateCcw className="h-4 w-4 mr-2" /> Reorder
            </Button>
            {canCancel && (
              <Button variant="destructive" className="w-full" loading={cancelling} onClick={handleCancel}>
                <XCircle className="h-4 w-4 mr-2" /> Cancel Order
              </Button>
            )}
            {order.status === 'DELIVERED' && (
              <Button variant="outline" className="w-full" onClick={() => navigate('/categories/medicines')}>
                <RotateCcw className="h-4 w-4 mr-2" /> Reorder
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
